<template>
    <div
        class="field-filepicker field-wrap"
        v-bind:class="{
            'field-filepicker--has-content': fileIsAvailable
        }"
    >
        <label v-if="label && !removeQuestionActive" class="field-filepicker__label">
            {{ label }}<span v-if="required" class="field-text__label-required">&nbsp;*</span>
        </label>

        <div class="field-filepicker__wrap">
            <div
                class="field-filepicker__status"
                v-bind:class="{
                    'field-filepicker__status--remove-question-active': removeQuestionActive,
                    'field-filepicker__status--link-active': !fileIsAvailable,
                    'field-filepicker__status--missing': required && !fileIsAvailable,
                    'field-filepicker__status--type-image': acceptIsImage(accept),
                    'field-filepicker__status--type-file': !acceptIsImage(accept)
                }"
                v-on="!fileIsAvailable ? { click: open } : {}"
            >
                <span
                    v-if="fileIsAvailable && !removeQuestionActive"
                    class="field-filepicker__filename"
                    >{{
                        (getFileExtension(getFileData('id')) || '').toUpperCase() + '-Datei'
                    }}</span
                >

                <span
                    v-if="removeQuestionActive"
                    class="field-filepicker__option-delete-question"
                    >{{
                        acceptIsImage(accept)
                            ? 'Bild wirklich entfernen?'
                            : 'Dokument wirklich entfernen?'
                    }}</span
                >
                <div v-if="fileIsAvailable" class="field-filepicker__options">
                    <div
                        class="field-filepicker__options-inner"
                        v-bind:style="
                            'background-image: url(\'' +
                                generateMediaUrl({
                                    id: getFileData('id'),
                                    w: getFileData('width'),
                                    h: getFileData('height')
                                }) +
                                '\')'
                        "
                    ></div>
                    <template v-if="hideRemove !== true">
                        <div v-if="!removeQuestionActive">
                            <a
                                v-on:click.prevent="remove()"
                                href="#"
                                class="field-filepicker__option-delete"
                                >Löschen</a
                            >
                        </div>
                        <div v-else>
                            <a
                                v-on:click.prevent="removeConfirm()"
                                href="#"
                                class="field-filepicker__option-delete-yes"
                                >OK</a
                            >
                            <a
                                v-on:click.prevent="removeDeny()"
                                href="#"
                                class="field-filepicker__option-delete-no"
                                >NO</a
                            >
                        </div>
                    </template>
                </div>
            </div>
            <a
                v-on:click.prevent="open"
                href="#"
                class="field-filepicker__upload"
                :class="
                    'field-filepicker__upload--' + getFilemanagerFilesViewFromAccept(this.accept)
                "
                >Upload</a
            >
        </div>

        <div v-if="fileIsAvailable" class="field-filepicker__file">
            <template v-if="fileIsImage(getFileData('id')) && showFocus">
                <app-select
                    label="Vertikale Position Bild"
                    :options="{ top: 'Oben', center: 'Mitte', bottom: 'Unten' }"
                    :value="getFileData('position_y')"
                    @input="setFileData('position_y', $event)"
                ></app-select>
                <app-select
                    label="Horizontale Position Bild"
                    :options="{ left: 'Links', center: 'Mitte', right: 'Rechts' }"
                    :value="getFileData('position_x')"
                    @input="setFileData('position_x', $event)"
                ></app-select>
            </template>

            <template v-if="fileIsImage(getFileData('id')) && showScalingMode">
                <app-select
                    label="Bildskalierungsmodus"
                    :options="{ cover: 'Automatischer Beschnitt', contain: 'Kein Beschnitt' }"
                    :value="getFileData('background_size')"
                    @input="setFileData('background_size', $event)"
                ></app-select>
            </template>

            <template v-if="fileIsImage(getFileData('id')) && showTransparency">
                <app-number
                    label="Transparenz"
                    :value="getFileData('transparency')"
                    @input="setFileData('transparency', $event)"
                    :min="0"
                    :max="100"
                    :data-default="{ type: 'raw', value: transparencyDefault }"
                ></app-number>
            </template>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Select from '@/components/fields/simple/Select.vue';
import Number from '@/components/fields/simple/Number.vue';
import { ComplexFieldMixin } from '@/mixins/ComplexFieldMixin';

export default {
    data() {
        return {
            removeQuestionActive: false
        };
    },
    components: {
        'app-select': Select,
        'app-number': Number
    },
    methods: {
        ...mapActions(['openFilemanager', 'setProp', 'removeProp', 'addProp']),
        /* init */
        async init() {
            let cur = this.getProp(this.dataSource);
            if (
                this.dataDefault !== undefined &&
                'type' in this.dataDefault &&
                this.dataDefault.type === 'file' &&
                this.fileNamespace !== undefined &&
                cur === undefined
            ) {
                await this.setProp({
                    source: this.dataSource + '.id',
                    value: this.getRandomImageInNamespace(this.fileNamespace)
                });
            }
        },
        /* validate */
        validate(val) {
            let valid = true;
            if (val === undefined) {
                val = [];
            }
            if (!Array.isArray(val)) {
                val = [val];
            }
            if (this.required && (val.length === 0 || val[0] === null || val[0] === undefined || val[0] === '')) {
                valid = false;
            }
            return valid;
        },
        /* custom setters */
        async open() {
            this.removeQuestionActive = false;
            let files = await this.openFilemanager({
                source: this.dataSource,
                accept: this.accept,
                multiple: false
            });
            if (files !== null) {
                await this.componentDifferChange();
                await this.setProp({ source: this.dataSource, value: files });
                await this.componentDifferValidate();
            }
        },
        remove() {
            this.removeQuestionActive = true;
        },
        removeDeny() {
            this.removeQuestionActive = false;
        },
        async removeConfirm() {
            this.removeQuestionActive = false;
            await this.componentDifferChange();
            // do not entirely remove so that default images to not get reapplied
            //await this.removeProp({ source: this.dataSource });
            await this.setProp({ source: this.dataSource, value: null });
            await this.componentDifferValidate();
        },
        async setFileData(prop, value) {
            await this.componentDifferChange();
            await this.setProp({
                source: this.dataSource + (prop ? '.' + prop : ''),
                value: value
            });
            await this.componentDifferValidate();
        },
        /* custom getters */
        getFileData(prop = null) {
            return this.getProp(this.dataSource + (prop ? '.' + prop : ''));
        }
    },
    computed: {
        ...mapGetters([
            'getProp',
            'isShadowed',
            'generateMediaUrl',
            'fileIsImage',
            'acceptIsImage',
            'getFileFilename',
            'getFileExtension',
            'getRandomImageInNamespace',
            'getFilemanagerFilesViewFromAccept'
        ]),
        disabled() {
            return this.isShadowed(this.dataSource, this.dataPreset);
        },
        fileIsAvailable() {
            return (
                this.getFileData('id') !== undefined &&
                this.getFileData('id') !== null &&
                this.getFileData('id') !== ''
            );
        }
    },
    props: {
        label: String,
        accept: String,
        required: Boolean,
        dataSource: String,
        dataPreset: [String, Boolean],
        showFocus: Boolean,
        showTransparency: Boolean,
        showScalingMode: Boolean,
        transparencyDefault: Number,
        hideRemove: Boolean,
        dataDefault: Object,
        fileNamespace: String
    },
    mixins: [ComplexFieldMixin]
};
</script>
